import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/store';
import { checkSubscriptionAccess } from '@/features/billing/utils/subscriptionHelpers';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

/**
 * SubscriptionGuard - Enforces subscription access throughout the app
 *
 * Access rules:
 * - Active subscription: Full access
 * - Valid trial (not expired): Full access
 * - Grace period (3 days after expiry): Read-only access with banner
 * - No subscription or past grace period: Redirect to /subscribe
 *
 * The /subscribe page itself is always accessible to allow subscription.
 */
export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const currentOrganization = useStore((state) => state.currentOrganization);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentOrganization) {
      // No organization yet - user is likely in onboarding
      return;
    }

    // Always allow access to /subscribe page itself
    if (location.pathname === '/subscribe') {
      return;
    }

    // Check subscription access
    const access = checkSubscriptionAccess(currentOrganization);

    if (!access.hasAccess) {
      // No valid subscription and no grace period - redirect to subscribe
      navigate('/subscribe');
      return;
    }

    if (access.isGracePeriod) {
      // In grace period - allow read-only access
      // Banner will be shown via GracePeriodBanner component in app layout
      return;
    }

    // Has valid subscription (active or trialing) - allow full access
  }, [currentOrganization, location.pathname, navigate]);

  return <>{children}</>;
}
